'use client';

import React, { useState } from 'react';
import { ShoppingCart, Check, Plus, Minus, Wrench, MessageCircle } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { useCurrency } from '@/context/CurrencyContext';
import { buildProductWhatsAppLink } from '@/lib/whatsapp';

interface ProductAddToCartProps {
  product: {
    id: string;
    name: string;
    sku: string;
    sellingPrice: number;
    discountPrice?: number | null;
    coolingCapacityBtu?: number | null;
    brandName?: string;
    stockQuantity: number;
  };
  whatsappNumber?: string;
}

function WhatsAppIcon({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91C2.13 13.66 2.59 15.36 3.45 16.86L2.05 22L7.3 20.62C8.75 21.41 10.38 21.83 12.04 21.83C17.5 21.83 21.95 17.38 21.95 11.92C21.95 9.27 20.92 6.78 19.05 4.91C17.18 3.03 14.69 2 12.04 2ZM12.05 20.15C10.56 20.15 9.1 19.75 7.83 19L7.53 18.82L4.41 19.64L5.24 16.59L5.04 16.27C4.22 14.97 3.79 13.46 3.79 11.91C3.79 7.37 7.5 3.66 12.05 3.66C14.25 3.66 16.32 4.52 17.88 6.07C19.43 7.63 20.29 9.7 20.29 11.91C20.28 16.46 16.58 20.15 12.05 20.15ZM16.57 14.39C16.32 14.26 15.1 13.66 14.87 13.58C14.64 13.5 14.48 13.46 14.31 13.71C14.15 13.96 13.68 14.51 13.54 14.67C13.4 14.84 13.26 14.86 13.01 14.73C12.76 14.61 11.96 14.34 11.01 13.49C10.27 12.83 9.77 12.01 9.63 11.76C9.48 11.51 9.61 11.38 9.74 11.26C9.85 11.14 9.99 10.96 10.12 10.82C10.24 10.67 10.28 10.57 10.37 10.4C10.45 10.24 10.41 10.09 10.35 9.97C10.28 9.85 9.79 8.64 9.58 8.14C9.38 7.66 9.18 7.72 9.03 7.71C8.89 7.71 8.72 7.7 8.56 7.7C8.39 7.7 8.12 7.76 7.89 8.01C7.66 8.26 7.02 8.86 7.02 10.08C7.02 11.3 7.91 12.47 8.03 12.64C8.16 12.8 9.77 15.29 12.24 16.36C12.83 16.61 13.29 16.76 13.64 16.87C14.23 17.06 14.77 17.03 15.2 16.97C15.68 16.9 16.67 16.37 16.88 15.79C17.08 15.2 17.08 14.7 17.02 14.59C16.96 14.49 16.82 14.42 16.57 14.39Z" />
    </svg>
  );
}

export function ProductAddToCart({ product, whatsappNumber }: ProductAddToCartProps) {
  const { addItem } = useCart();
  const { format } = useCurrency();
  const [quantity, setQuantity] = useState(1);
  const [includeInstallation, setIncludeInstallation] = useState(false);
  const [added, setAdded] = useState(false);

  const activePrice = product.discountPrice && product.discountPrice > 0 
    ? product.discountPrice 
    : product.sellingPrice;
  const hasDiscount = product.discountPrice && product.discountPrice < product.sellingPrice;

  const handleAddToCart = () => {
    addItem({
      id: product.id,
      productId: product.id,
      name: product.name,
      sku: product.sku,
      price: activePrice,
      coolingCapacityBtu: product.coolingCapacityBtu,
      brandName: product.brandName,
    }, quantity);

    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const handleWhatsAppContact = () => {
    const formattedPrice = format(activePrice);
    const productUrl = typeof window !== 'undefined' ? window.location.href : undefined;

    const waLink = buildProductWhatsAppLink({
      productName: product.name,
      sku: product.sku,
      priceFormatted: formattedPrice,
      quantity,
      includeInstallation,
      productUrl,
      whatsappNumber,
    });

    window.open(waLink, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm space-y-5">
      {/* Price Display */}
      <div className="flex items-baseline justify-between border-b border-slate-100 pb-4">
        <div>
          <div className="text-3xl font-black text-slate-900 leading-none">
            {format(activePrice)}
          </div>
          {hasDiscount && (
            <div className="text-sm text-slate-400 line-through mt-1">
              {format(product.sellingPrice)}
            </div>
          )}
          <div className="text-xs text-slate-400 mt-1">Çmimi përfshin 20% TVSH</div>
        </div>

        <div className="text-right">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-50 text-emerald-700 text-xs font-bold border border-emerald-200">
            <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
            {product.stockQuantity > 0 ? `Në Gjendje (${product.stockQuantity} copë)` : 'Porosi me Kërkesë'}
          </span>
        </div>
      </div>

      {/* Optional Installation checkbox */}
      <div className="p-3.5 rounded-xl bg-sky-50/60 border border-sky-100 flex items-start gap-3">
        <input
          type="checkbox"
          id="installation"
          checked={includeInstallation}
          onChange={(e) => setIncludeInstallation(e.target.checked)}
          className="mt-0.5 rounded text-sky-600 focus:ring-sky-500 w-4 h-4 cursor-pointer"
        />
        <label htmlFor="installation" className="text-xs text-slate-700 cursor-pointer">
          <strong className="text-slate-900 block font-semibold flex items-center gap-1">
            <Wrench className="w-3.5 h-3.5 text-sky-600" />
            Kërkoj edhe montim profesional nga DYRMA Climate (+7,000 ALL)
          </strong>
          Përfshin 3 metra tubacion bakri të izoluar, kllapa, lidhje elektrike dhe vakumim profesional me pompë vakuumi.
        </label>
      </div>

      {/* Action Buttons: Add to Cart & WhatsApp */}
      <div className="space-y-3 pt-1">
        {/* Row 1: Quantity & Add to Cart button */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex items-center justify-between border border-slate-200 rounded-xl p-1 bg-slate-50 w-full sm:w-36 flex-shrink-0">
            <button
              onClick={() => setQuantity((q) => Math.max(1, q - 1))}
              className="w-9 h-9 rounded-lg bg-white shadow-sm flex items-center justify-center text-slate-600 hover:bg-slate-100 transition-colors"
              title="Zvogëlo sasinë"
            >
              <Minus className="w-4 h-4" />
            </button>
            <span className="font-bold text-sm text-slate-900">{quantity}</span>
            <button
              onClick={() => setQuantity((q) => q + 1)}
              className="w-9 h-9 rounded-lg bg-white shadow-sm flex items-center justify-center text-slate-600 hover:bg-slate-100 transition-colors"
              title="Rrit sasinë"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>

          <button
            onClick={handleAddToCart}
            className={`flex-grow py-3.5 px-6 rounded-xl font-bold text-sm flex items-center justify-center gap-2 shadow-md hover:shadow-lg transition-all ${
              added
                ? 'bg-emerald-600 text-white shadow-emerald-600/20'
                : 'bg-slate-900 hover:bg-sky-600 text-white shadow-slate-900/20'
            }`}
          >
            {added ? (
              <>
                <Check className="w-5 h-5" />
                U shtua në shportë!
              </>
            ) : (
              <>
                <ShoppingCart className="w-5 h-5 text-sky-400" />
                Shto në Shportë
              </>
            )}
          </button>
        </div>

        {/* Row 2: WhatsApp Quick Order / Contact Button */}
        <button
          onClick={handleWhatsAppContact}
          className="w-full py-3.5 px-6 rounded-xl font-bold text-sm flex items-center justify-center gap-2.5 bg-[#25D366] hover:bg-[#20ba59] text-white shadow-md hover:shadow-[#25D366]/25 transition-all group"
          title="Bisedo dhe porosit drejtpërdrejt në WhatsApp"
        >
          <WhatsAppIcon className="w-5 h-5 transition-transform group-hover:scale-110" />
          <span>Porosit ose Kontakto në WhatsApp</span>
        </button>

        <p className="text-[11px] text-center text-slate-400 flex items-center justify-center gap-1.5 pt-1">
          <span>⚡ Bisedoni direkt me inxhinierët tanë të klimës (+355 69 367 3867)</span>
        </p>
      </div>
    </div>
  );
}
